// src/components/MagazineAdmin.js
import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  addDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  Package,
  TrendingUp,
  MapPin,
  Edit2,
  Save,
  X,
  Plus,
  Trash2,
  BookOpen,
  Users,
  Filter,
  CheckCircle,
  XCircle,
  Download,
  Upload,
} from "lucide-react";

export default function MagazineAdmin() {
  const [editions, setEditions] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [editingEdition, setEditingEdition] = useState(null);
  const [editingCenter, setEditingCenter] = useState(null);
  const [newEdition, setNewEdition] = useState({
    name: "",
    year: "2025-26",
    editionNumber: "",
    totalCopies: 0,
    packed: 0,
    sent: 0,
    status: "In Progress",
  });
  const [newCenter, setNewCenter] = useState({
    name: "",
    location: "Kanhangad, Kerala, IN",
    subscriptions: 0,
    contactPerson: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch editions
      const editionsQuery = query(
        collection(db, "magazineeditions"),
        orderBy("createdAt", "desc")
      );
      const editionsSnap = await getDocs(editionsQuery);
      const editionsData = editionsSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setEditions(editionsData);

      // Fetch centers
      const centersQuery = query(
        collection(db, "subscriptioncenters"),
        orderBy("name", "asc")
      );
      const centersSnap = await getDocs(centersQuery);
      const centersData = centersSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setCenters(centersData);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const addEdition = async () => {
    if (!newEdition.name || !newEdition.editionNumber) {
      alert("Please fill in edition name and number");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "magazineeditions"), {
        ...newEdition,
        totalCopies: Number(newEdition.totalCopies),
        packed: Number(newEdition.packed),
        sent: Number(newEdition.sent),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewEdition({
        name: "",
        year: "2025-26",
        editionNumber: "",
        totalCopies: 0,
        packed: 0,
        sent: 0,
        status: "In Progress",
      });

      await fetchData();
    } catch (error) {
      console.error("Error adding edition:", error);
      alert("Failed to add edition");
    }
    setLoading(false);
  };

  const updateEdition = async (id, data) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, "magazineeditions", id), {
        ...data,
        totalCopies: Number(data.totalCopies),
        packed: Number(data.packed),
        sent: Number(data.sent),
        updatedAt: serverTimestamp(),
      });
      await fetchData();
      setEditingEdition(null);
    } catch (error) {
      console.error("Error updating edition:", error);
      alert("Failed to update edition");
    }
    setLoading(false);
  };

  const deleteEdition = async (id) => {
    if (!window.confirm("Are you sure you want to delete this edition?"))
      return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "magazineeditions", id));
      await fetchData();
    } catch (error) {
      console.error("Error deleting edition:", error);
      alert("Failed to delete edition");
    }
    setLoading(false);
  };

  const addCenter = async () => {
    if (!newCenter.name || !newCenter.location) {
      alert("Please fill in center name and location");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "subscriptioncenters"), {
        ...newCenter,
        subscriptions: Number(newCenter.subscriptions),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewCenter({
        name: "",
        location: "Kanhangad, Kerala, IN",
        subscriptions: 0,
        contactPerson: "",
        email: "",
        phone: "",
      });

      await fetchData();
    } catch (error) {
      console.error("Error adding center:", error);
      alert("Failed to add center");
    }
    setLoading(false);
  };

  const updateCenter = async (id, data) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, "subscriptioncenters", id), {
        ...data,
        subscriptions: Number(data.subscriptions),
        updatedAt: serverTimestamp(),
      });
      await fetchData();
      setEditingCenter(null);
    } catch (error) {
      console.error("Error updating center:", error);
      alert("Failed to update center");
    }
    setLoading(false);
  };

  const deleteCenter = async (id) => {
    if (!window.confirm("Are you sure you want to delete this center?"))
      return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "subscriptioncenters", id));
      await fetchData();
    } catch (error) {
      console.error("Error deleting center:", error);
      alert("Failed to delete center");
    }
    setLoading(false);
  };

  const stats = {
    totalEditions: editions.length,
    currentYearEditions: editions.filter((e) => e.year === "2025-26").length,
    totalCenters: centers.length,
    totalSubscriptions: centers.reduce((sum, c) => sum + (Number(c.subscriptions) || 0), 0),
    totalPacked: editions.reduce((sum, e) => sum + (Number(e.packed) || 0), 0),
    totalSent: editions.reduce((sum, e) => sum + (Number(e.sent) || 0), 0),
  };

  if (loading && editions.length === 0 && centers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Magazine Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 shadow-xl">
        <h1 className="text-3xl md:text-5xl font-bold mb-2">📚 Magazine Management</h1>
        <p className="text-blue-100 text-lg">Track editions, subscriptions, and distribution</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
        <div className="bg-white border-2 border-blue-200 rounded-xl p-4 shadow-md hover:shadow-lg transition">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={20} className="text-blue-600" />
            <p className="text-xs text-gray-600 font-medium">Total Editions</p>
          </div>
          <p className="text-3xl font-bold text-blue-700">{stats.totalEditions}</p>
        </div>

        <div className="bg-white border-2 border-green-200 rounded-xl p-4 shadow-md hover:shadow-lg transition">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={20} className="text-green-600" />
            <p className="text-xs text-gray-600 font-medium">2025-26 Editions</p>
          </div>
          <p className="text-3xl font-bold text-green-700">{stats.currentYearEditions}</p>
        </div>

        <div className="bg-white border-2 border-purple-200 rounded-xl p-4 shadow-md hover:shadow-lg transition">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={20} className="text-purple-600" />
            <p className="text-xs text-gray-600 font-medium">Centers</p>
          </div>
          <p className="text-3xl font-bold text-purple-700">{stats.totalCenters}</p>
        </div>

        <div className="bg-white border-2 border-orange-200 rounded-xl p-4 shadow-md hover:shadow-lg transition">
          <div className="flex items-center gap-2 mb-2">
            <Users size={20} className="text-orange-600" />
            <p className="text-xs text-gray-600 font-medium">Subscriptions</p>
          </div>
          <p className="text-3xl font-bold text-orange-700">{stats.totalSubscriptions}</p>
        </div>

        <div className="bg-white border-2 border-indigo-200 rounded-xl p-4 shadow-md hover:shadow-lg transition">
          <div className="flex items-center gap-2 mb-2">
            <Package size={20} className="text-indigo-600" />
            <p className="text-xs text-gray-600 font-medium">Packed</p>
          </div>
          <p className="text-3xl font-bold text-indigo-700">{stats.totalPacked}</p>
        </div>

        <div className="bg-white border-2 border-pink-200 rounded-xl p-4 shadow-md hover:shadow-lg transition">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={20} className="text-pink-600" />
            <p className="text-xs text-gray-600 font-medium">Sent</p>
          </div>
          <p className="text-3xl font-bold text-pink-700">{stats.totalSent}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: "overview", label: "Overview", icon: TrendingUp },
          { id: "editions", label: "Editions", icon: BookOpen },
          { id: "centers", label: "Centers", icon: MapPin },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`px-4 md:px-6 py-3 rounded-xl font-semibold transition flex items-center gap-2 whitespace-nowrap ${
              activeTab === id
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105"
                : "bg-white text-gray-700 hover:bg-gray-100 shadow-md"
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Current Year Editions */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <BookOpen className="text-blue-600" size={28} />
              2025-26 Editions Status
            </h2>
            {editions.filter((e) => e.year === "2025-26").length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <BookOpen size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-lg">No editions for 2025-26 yet</p>
                <button
                  onClick={() => setActiveTab("editions")}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Add First Edition
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {editions
                  .filter((e) => e.year === "2025-26")
                  .map((edition) => (
                    <div
                      key={edition.id}
                      className="p-5 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-xl text-gray-900">{edition.name}</h3>
                          <p className="text-sm text-gray-600">Edition #{edition.editionNumber}</p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-bold ${
                            edition.status === "Completed"
                              ? "bg-green-500 text-white"
                              : edition.status === "In Progress"
                              ? "bg-yellow-400 text-gray-900"
                              : "bg-gray-300 text-gray-700"
                          }`}
                        >
                          {edition.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <p className="text-xs text-gray-500 mb-1">Total Copies</p>
                          <p className="text-2xl font-bold text-blue-600">{edition.totalCopies}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <p className="text-xs text-gray-500 mb-1">Packed</p>
                          <p className="text-2xl font-bold text-indigo-600">{edition.packed}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <p className="text-xs text-gray-500 mb-1">Sent</p>
                          <p className="text-2xl font-bold text-green-600">{edition.sent}</p>
                        </div>
                      </div>
                      <div className="mt-3 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all"
                          style={{
                            width: `${
                              edition.totalCopies > 0
                                ? (edition.sent / edition.totalCopies) * 100
                                : 0
                            }%`,
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 text-right">
                        {edition.totalCopies > 0
                          ? Math.round((edition.sent / edition.totalCopies) * 100)
                          : 0}
                        % Distributed
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Top Centers by Subscriptions */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <MapPin className="text-purple-600" size={28} />
              Subscription Centers
            </h2>
            {centers.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <MapPin size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-lg">No centers added yet</p>
                <button
                  onClick={() => setActiveTab("centers")}
                  className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  Add First Center
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {centers
                  .sort((a, b) => (b.subscriptions || 0) - (a.subscriptions || 0))
                  .map((center) => (
                    <div
                      key={center.id}
                      className="p-5 bg-gradient-to-r from-gray-50 to-purple-50 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition flex justify-between items-center"
                    >
                      <div className="flex-1">
                        <h3 className="font-bold text-xl text-gray-900">{center.name}</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                          <MapPin size={14} />
                          {center.location}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-purple-600">{center.subscriptions}</p>
                        <p className="text-xs text-gray-500 font-medium">Subscriptions</p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editions Tab */}
      {activeTab === "editions" && (
        <div className="space-y-6">
          {/* Add New Edition Form */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Plus className="text-blue-600" />
              Add New Edition
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Edition Name"
                value={newEdition.name}
                onChange={(e) => setNewEdition({ ...newEdition, name: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Edition Number"
                value={newEdition.editionNumber}
                onChange={(e) => setNewEdition({ ...newEdition, editionNumber: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Year (e.g., 2025-26)"
                value={newEdition.year}
                onChange={(e) => setNewEdition({ ...newEdition, year: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Total Copies"
                value={newEdition.totalCopies}
                onChange={(e) => setNewEdition({ ...newEdition, totalCopies: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Packed"
                value={newEdition.packed}
                onChange={(e) => setNewEdition({ ...newEdition, packed: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Sent"
                value={newEdition.sent}
                onChange={(e) => setNewEdition({ ...newEdition, sent: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={newEdition.status}
                onChange={(e) => setNewEdition({ ...newEdition, status: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Planning">Planning</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
              <button
                onClick={addEdition}
                disabled={loading}
                className="lg:col-span-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold transition shadow-md flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Edition
              </button>
            </div>
          </div>

          {/* Editions List */}
          {editions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
              <BookOpen size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-xl font-medium">No editions yet</p>
              <p className="text-gray-400 mt-2">Add your first magazine edition above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {editions.map((edition) => (
                <div
                  key={edition.id}
                  className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-blue-300 transition"
                >
                  {editingEdition?.id === edition.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={editingEdition.name}
                          onChange={(e) => setEditingEdition({ ...editingEdition, name: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="text"
                          value={editingEdition.editionNumber}
                          onChange={(e) => setEditingEdition({ ...editingEdition, editionNumber: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="text"
                          value={editingEdition.year}
                          onChange={(e) => setEditingEdition({ ...editingEdition, year: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="number"
                          value={editingEdition.totalCopies}
                          onChange={(e) => setEditingEdition({ ...editingEdition, totalCopies: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="number"
                          value={editingEdition.packed}
                          onChange={(e) => setEditingEdition({ ...editingEdition, packed: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="number"
                          value={editingEdition.sent}
                          onChange={(e) => setEditingEdition({ ...editingEdition, sent: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <select
                          value={editingEdition.status}
                          onChange={(e) => setEditingEdition({ ...editingEdition, status: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        >
                          <option value="Planning">Planning</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => updateEdition(edition.id, editingEdition)}
                          className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                        >
                          <Save size={18} />
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingEdition(null)}
                          className="px-5 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                        >
                          <X size={18} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{edition.name}</h3>
                          <p className="text-gray-600 font-medium">
                            Edition #{edition.editionNumber} · {edition.year}
                          </p>
                        </div>
                        <span
                          className={`px-4 py-2 rounded-full text-sm font-bold shadow-md ${
                            edition.status === "Completed"
                              ? "bg-green-500 text-white"
                              : edition.status === "In Progress"
                              ? "bg-yellow-400 text-gray-900"
                              : "bg-gray-300 text-gray-700"
                          }`}
                        >
                          {edition.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-600 mb-1 font-medium">Total Copies</p>
                          <p className="text-3xl font-bold text-blue-700">{edition.totalCopies}</p>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-600 mb-1 font-medium">Packed</p>
                          <p className="text-3xl font-bold text-indigo-700">{edition.packed}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-600 mb-1 font-medium">Sent</p>
                          <p className="text-3xl font-bold text-green-700">{edition.sent}</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setEditingEdition(edition)}
                          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md"
                        >
                          <Edit2 size={18} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteEdition(edition.id)}
                          className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md"
                        >
                          <Trash2 size={18} />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Centers Tab */}
      {activeTab === "centers" && (
        <div className="space-y-6">
          {/* Add New Center Form */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Plus className="text-purple-600" />
              Add New Center
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Center Name"
                value={newCenter.name}
                onChange={(e) => setNewCenter({ ...newCenter, name: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Location"
                value={newCenter.location}
                onChange={(e) => setNewCenter({ ...newCenter, location: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Number of Subscriptions"
                value={newCenter.subscriptions}
                onChange={(e) => setNewCenter({ ...newCenter, subscriptions: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Contact Person"
                value={newCenter.contactPerson}
                onChange={(e) => setNewCenter({ ...newCenter, contactPerson: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Email"
                value={newCenter.email}
                onChange={(e) => setNewCenter({ ...newCenter, email: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={newCenter.phone}
                onChange={(e) => setNewCenter({ ...newCenter, phone: e.target.value })}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={addCenter}
                disabled={loading}
                className="md:col-span-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg font-semibold transition shadow-md flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Center
              </button>
            </div>
          </div>

          {/* Centers List */}
          {centers.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
              <MapPin size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-xl font-medium">No centers yet</p>
              <p className="text-gray-400 mt-2">Add your first subscription center above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {centers.map((center) => (
                <div
                  key={center.id}
                  className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-purple-300 transition"
                >
                  {editingCenter?.id === center.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={editingCenter.name}
                          onChange={(e) => setEditingCenter({ ...editingCenter, name: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="text"
                          value={editingCenter.location}
                          onChange={(e) => setEditingCenter({ ...editingCenter, location: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="number"
                          value={editingCenter.subscriptions}
                          onChange={(e) => setEditingCenter({ ...editingCenter, subscriptions: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="text"
                          value={editingCenter.contactPerson}
                          onChange={(e) => setEditingCenter({ ...editingCenter, contactPerson: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="email"
                          value={editingCenter.email}
                          onChange={(e) => setEditingCenter({ ...editingCenter, email: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                        <input
                          type="tel"
                          value={editingCenter.phone}
                          onChange={(e) => setEditingCenter({ ...editingCenter, phone: e.target.value })}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => updateCenter(center.id, editingCenter)}
                          className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                        >
                          <Save size={18} />
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingCenter(null)}
                          className="px-5 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                        >
                          <X size={18} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{center.name}</h3>
                          <p className="text-gray-600 font-medium flex items-center gap-1 mt-1">
                            <MapPin size={16} />
                            {center.location}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-purple-700">{center.subscriptions}</p>
                          <p className="text-sm text-gray-500 font-medium">Subscriptions</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-600 mb-1 font-medium">Contact Person</p>
                          <p className="text-lg font-bold text-purple-700">{center.contactPerson || "N/A"}</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-600 mb-1 font-medium">Email</p>
                          <p className="text-lg font-bold text-purple-700">{center.email || "N/A"}</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-600 mb-1 font-medium">Phone</p>
                          <p className="text-lg font-bold text-purple-700">{center.phone || "N/A"}</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setEditingCenter(center)}
                          className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md"
                        >
                          <Edit2 size={18} />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteCenter(center.id)}
                          className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md"
                        >
                          <Trash2 size={18} />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}